/**
 * Axis visuals — the design system's axes rendered AS DESIGN, not as text.
 *
 * Every option of every axis is backed by real preset data
 * (@cogo/design-presets): base colors and themes carry complete CSS color
 * strings (oklch), radii carry rem values, fonts carry CSS stacks. These
 * components render that data directly — nothing is invented, so a swatch
 * can never disagree with what the generated app will actually receive.
 *
 * Used by DesignSystemControls (theme editor rail) and AxisRow
 * (source-derived axis list) so both pickers show the same visual identity.
 *
 * ── THE TOOLTIPS ARE COPY, AND THEY ARE HOVER-ONLY ──────────────────────────
 * A swatch is a coloured box: it has no text node at all, so everything it
 * says to a reader it says through `title`. That is invisible to a sweep that
 * walks `textContent`, which is exactly how `neutral step 1` / `Zinc light`
 * survived the lane that localized every other string on
 * /studio/design-system. The words now come from the active locale's
 * `strings.designSystemAxes.swatchTooltips`; the preset's own NAME is passed
 * through untranslated, because `Zinc` is `Zinc` in every language.
 *
 * `FontSwatch` is the deliberate exception: its tooltip is the preset's CSS
 * font stack verbatim, which is a VALUE (the exact string the generated app
 * receives), not copy. See `SwatchTooltipCopy` in axes-copy.ts.
 */

import { useStudioLocale } from '@/i18n/use-studio-locale'
import { radiusOptionLabel } from '../source-derived/axes-copy'
import {
  BASE_COLORS_BY_NAME,
  CHART_PALETTES,
  FONTS,
  RADII_LIST,
  THEMES,
} from '../lib/shadcn-presets'

const CHART_SLOTS = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5'] as const

function Dot({ color, title }: { color: string; title?: string }) {
  return (
    <span
      aria-hidden
      title={title}
      className="inline-block h-4 w-4 shrink-0 rounded-full border border-foreground/15"
      style={{ backgroundColor: color }}
    />
  )
}

/** Neutral ramp: background → muted → foreground of the palette. */
export function BaseColorSwatch({ name }: { name: string }) {
  const { strings } = useStudioLocale()
  const preset = BASE_COLORS_BY_NAME[name]
  if (!preset) return null
  const tooltips = strings.designSystemAxes.swatchTooltips
  const steps = [
    preset.light['background'],
    preset.light['muted'],
    preset.light['muted-foreground'],
    preset.light['foreground'],
  ].filter((v): v is string => typeof v === 'string')
  return (
    <span className="inline-flex items-center gap-1" data-testid="axis-swatch-baseColor">
      {steps.map((color, index) => (
        <Dot key={index} color={color} title={tooltips.baseColorStep(name, index + 1)} />
      ))}
    </span>
  )
}

/** Primary hue of the theme preset, light + dark. */
export function ThemeSwatch({ name }: { name: string }) {
  const { strings } = useStudioLocale()
  const preset = THEMES[name]
  if (!preset) return null
  const tooltips = strings.designSystemAxes.swatchTooltips
  const light = preset.light['primary']
  const dark = preset.dark['primary']
  return (
    <span className="inline-flex items-center gap-1" data-testid="axis-swatch-theme">
      {typeof light === 'string' ? (
        <Dot color={light} title={tooltips.themeLight(name)} />
      ) : null}
      {typeof dark === 'string' ? (
        <Dot color={dark} title={tooltips.themeDark(name)} />
      ) : null}
    </span>
  )
}

/** The five chart slots as a strip. */
export function ChartSwatch({ name }: { name: string }) {
  const preset = CHART_PALETTES[name]
  if (!preset) return null
  return (
    <span
      className="inline-flex items-center overflow-hidden rounded-md border border-foreground/15"
      data-testid="axis-swatch-chart"
      aria-hidden
    >
      {CHART_SLOTS.map((slot) => {
        const color = preset.light[slot]
        return typeof color === 'string' ? (
          <span
            key={slot}
            className="inline-block h-4 w-3.5"
            style={{ backgroundColor: color }}
          />
        ) : null
      })}
    </span>
  )
}

/** A square whose top-left corner carries the actual radius value. */
export function RadiusSwatch({ name }: { name: string }) {
  const { strings } = useStudioLocale()
  const preset = RADII_LIST.find((entry) => entry.name === name)
  if (!preset) return null
  const copy = strings.designSystemAxes
  return (
    <span
      aria-hidden
      data-testid="axis-swatch-radius"
      title={copy.swatchTooltips.radius(
        radiusOptionLabel({ value: preset.name, label: preset.label }, copy),
        preset.value || '0',
      )}
      className="inline-block h-5 w-5 shrink-0 border-l-2 border-t-2 border-foreground/60 bg-muted"
      style={{ borderTopLeftRadius: preset.value || '0' }}
    />
  )
}

/**
 * "Ag" specimen rendered in the option's real CSS stack.
 *
 * The tooltip is that stack verbatim — `"Geist", system-ui, sans-serif` — and
 * stays untranslated on purpose: it is the exact value the generated app
 * receives, so wrapping it in a localized noun would put words around it that
 * the data does not support. It is a value, like `cogo.xyz`, not copy.
 */
export function FontSwatch({ name }: { name: string }) {
  const preset = FONTS[name]
  if (!preset) return null
  return (
    <span
      aria-hidden
      data-testid="axis-swatch-font"
      title={preset.value}
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-foreground/15 bg-background px-1 text-[13px] leading-none text-foreground"
      style={{ fontFamily: preset.value }}
    >
      Ag
    </span>
  )
}

/**
 * The current value of one axis, rendered visually. `axis` uses the 6-axis
 * schema keys; `style` has no client-side visual identity (its CSS bodies are
 * not all shipped — see shadcn-presets STYLES note), so it renders nothing
 * rather than a fabricated preview.
 */
export function AxisValueSwatch({ axis, value }: { axis: string; value: string }) {
  switch (axis) {
    case 'baseColor':
      return <BaseColorSwatch name={value} />
    case 'theme':
      return <ThemeSwatch name={value} />
    case 'chart':
      return <ChartSwatch name={value} />
    case 'radius':
      return <RadiusSwatch name={value} />
    case 'font':
      return <FontSwatch name={value} />
    default:
      return null
  }
}
